import type { NetworkStatus } from '@tunasong/models'
import { networkBus, useOnlineStatus } from '@tunasong/models'
import type { FC, PropsWithChildren } from 'react'
import { useEffect, useRef, useState } from 'react'
import { NetworkStatusContext } from './network-status-context.js'

const RTK_FAILURE_WINDOW_MS = 10_000
const RTK_FAILURE_THRESHOLD = 3
/**
 * Grace period before a dropped WebSocket surfaces the "Reconnecting…" banner
 * (GH #1605). Most drops reconnect within a second or two; showing the banner
 * immediately just makes it flicker in normal use. If the socket comes back
 * inside this window, the banner never appears.
 */
const RECONNECT_GRACE_MS = 12_000

export const NetworkStatusProvider: FC<PropsWithChildren> = props => {
  const { children } = props
  const online = useOnlineStatus()
  const [wsConnected, setWsConnected] = useState(true)
  const [degraded, setDegraded] = useState(false)
  const failureTimestamps = useRef<number[]>([])
  const [hasEverBeenOnline, setHasEverBeenOnline] = useState(false)
  const [lastContactMs, setLastContactMs] = useState<number | null>(null)
  const [reconnectGraceElapsed, setReconnectGraceElapsed] = useState(false)

  useEffect(() => {
    const off = networkBus.on('ws-status-changed', ({ status }) => {
      setWsConnected(status === 'connected')
      if (status === 'connected') {
        setLastContactMs(Date.now())
      }
    })
    return off
  }, [])

  useEffect(() => {
    const off = networkBus.on('rtk-query-result', ({ ok }) => {
      const now = Date.now()
      if (ok) {
        failureTimestamps.current = []
        setDegraded(false)
        setLastContactMs(now)
        return
      }
      const stamps = failureTimestamps.current.filter(t => now - t < RTK_FAILURE_WINDOW_MS)
      stamps.push(now)
      failureTimestamps.current = stamps
      if (stamps.length >= RTK_FAILURE_THRESHOLD) {
        setDegraded(true)
      }
    })
    return off
  }, [])

  // Hold off on "Reconnecting…" until the WebSocket has stayed down past the
  // grace period (GH #1605); a quick reconnect resets the flag before it fires.
  useEffect(() => {
    if (wsConnected) {
      setReconnectGraceElapsed(false)
      return
    }
    const timer = setTimeout(() => setReconnectGraceElapsed(true), RECONNECT_GRACE_MS)
    return () => clearTimeout(timer)
  }, [wsConnected])

  let status: NetworkStatus = 'online'
  if (!online) {
    status = 'offline'
  } else if (!wsConnected && reconnectGraceElapsed) {
    status = 'reconnecting'
  } else if (degraded) {
    status = 'degraded'
  }

  useEffect(() => {
    if (status === 'online' && !hasEverBeenOnline) {
      setHasEverBeenOnline(true)
    }
  }, [status, hasEverBeenOnline])

  return (
    <NetworkStatusContext.Provider value={{ status, lastContactMs, hasEverBeenOnline }}>
      {children}
    </NetworkStatusContext.Provider>
  )
}
